/**
 * systemTemplates slice — thin shared state for the System Task Templates
 * super-admin page. Keeps selection + inline-edit drafts in sync across the
 * four tabs without requiring prop-drilling through the tab layout.
 *
 * Intentionally minimal. Heavy server state (the template list itself) stays
 * local to LibraryTab via useState + callApi — we don't want to persist it
 * in Redux because it goes stale quickly and doesn't need cross-tab sharing.
 *
 * selectedTemplateIds: used by Library → Push-to-Practices and
 *   Library → Import/Export "export selected" shortcuts.
 * dirtyEdits: staged inline edits on Library cards; cleared on save/discard.
 */
import { createSlice, createSelector } from '@reduxjs/toolkit';

const initialState = {
    // Set<string> stored as plain array so Redux can serialize it.
    // Use the selectors below to convert back to a Set where needed.
    selectedTemplateIds: [],
    // { [templateId: string]: { name?: string, status?: string, applies_to_statuses?: string[] } }
    dirtyEdits: {},
};

const slice = createSlice({
    name: 'systemTemplates',
    initialState,
    reducers: {
        setSelectedIds(state, action) {
            // action.payload: string[] (array of template id strings)
            state.selectedTemplateIds = action.payload;
        },
        toggleSelectedId(state, action) {
            const id = String(action.payload);
            const idx = state.selectedTemplateIds.indexOf(id);
            if (idx === -1) {
                state.selectedTemplateIds.push(id);
            } else {
                state.selectedTemplateIds.splice(idx, 1);
            }
        },
        clearSelectedIds(state) {
            state.selectedTemplateIds = [];
        },
        stageDirtyEdit(state, action) {
            // action.payload: { id: string, field: string, value: any }
            const { id, field, value } = action.payload;
            if (!state.dirtyEdits[id]) state.dirtyEdits[id] = {};
            state.dirtyEdits[id][field] = value;
        },
        clearDirtyEdit(state, action) {
            // action.payload: template id string
            delete state.dirtyEdits[String(action.payload)];
        },
        clearAllDirtyEdits(state) {
            state.dirtyEdits = {};
        },
    },
});

export default slice.reducer;

export const {
    setSelectedIds,
    toggleSelectedId,
    clearSelectedIds,
    stageDirtyEdit,
    clearDirtyEdit,
    clearAllDirtyEdits,
} = slice.actions;

// Selectors
// Memoized: returns the same Set instance until the underlying array changes,
// so useSelector consumers (and useEffect dep arrays) don't re-run on every store tick.
const _selectIdsArray = (state) => state.systemTemplates?.selectedTemplateIds || [];
export const selectSelectedIds = createSelector(
    [_selectIdsArray],
    (ids) => new Set(ids)
);

export const selectDirtyEdits = (state) =>
    state.systemTemplates?.dirtyEdits || {};

export const selectDirtyCount = (state) =>
    Object.keys(state.systemTemplates?.dirtyEdits || {}).length;
